import * as THREE from "three";
import { defineModel, type Part, type Variant } from "../../lib";

// A shell crater from no-man's-land. The ground plane is flat, so the crater
// is built entirely above y=0: a ring of angled mud clods forms the raised
// rim around a shallow dark disc that reads as the pit, with a little debris
// thrown clear. "dry" shows bare churned earth; "waterlogged" fills the pit
// with a murky water disc.
const MUD = "#5d4a33";
const DARK_MUD = "#46392a";
const DEAD = "#4f4238";
const WATER = "#3e4a42";

const RIM_R = 0.31; // radius of the clod ring

// Eight mud clods around the rim, alternating tone, each tipped outward a
// little so the ring reads as heaped, blasted earth rather than a wall.
function rim(): Part[] {
  const clods: Part[] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const x = Math.cos(a) * RIM_R;
    const z = Math.sin(a) * RIM_R;
    const big = i % 2 === 0;
    clods.push({
      geometry: new THREE.IcosahedronGeometry(big ? 0.11 : 0.09, 0),
      color: big ? MUD : DARK_MUD,
      position: [x, big ? 0.07 : 0.055, z],
      rotation: [0.25, a, -0.2],
      scale: [1.25, 0.75, 1.0],
    });
  }
  return clods;
}

function debris(): Part[] {
  return [
    // A splintered board and small clods thrown clear of the rim
    { geometry: new THREE.BoxGeometry(0.2, 0.025, 0.05), color: DEAD,
      position: [0.36, 0.02, -0.3], rotation: [0, 0.7, 0.1] },
    { geometry: new THREE.IcosahedronGeometry(0.045, 0), color: DARK_MUD, position: [-0.38, 0.03, 0.26] },
    { geometry: new THREE.IcosahedronGeometry(0.035, 0), color: MUD, position: [-0.3, 0.025, -0.36] },
    { geometry: new THREE.IcosahedronGeometry(0.04, 0), color: DARK_MUD, position: [0.4, 0.028, 0.22] },
  ];
}

// The shallow pit inside the rim.
function pit(color: string, h: number): Part {
  return { geometry: new THREE.CylinderGeometry(0.27, 0.29, h, 16), color, position: [0, h / 2, 0] };
}

const variants: Variant[] = [
  {
    id: "dry",
    label: "Dry",
    description: "Bare churned earth inside the rim of clods.",
    parts: () => [pit(DARK_MUD, 0.025), ...rim(), ...debris()],
  },
  {
    id: "waterlogged",
    label: "Waterlogged",
    description: "The pit filled with murky standing water.",
    parts: () => [pit(DARK_MUD, 0.02), pit(WATER, 0.045), ...rim(), ...debris()],
  },
];

export const shellCrater = defineModel({
  id: "shell-crater",
  batchId: "wwi",
  author: "Fable 5",
  name: "Shell Crater",
  tags: ["wwi", "scenery", "crater", "battlefield"],
  summary: "A shell crater: a raised rim of mud clods around a shallow dark pit, debris scattered.",
  footprint: 0.45,
  tiles: { w: 1, h: 1 },
  variants,
  animations: [],
});
